import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/use-auth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function PortalCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading: authLoading, dataLoaded, getRedirectPath } = useAuth();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');
  const [redirectAttempts, setRedirectAttempts] = useState(0);

  useEffect(() => {
    const token = searchParams.get('token');
    
    if (!token) {
      setStatus('error');
      setMessage('Token de sessão não encontrado na URL');
      return;
    }

    handlePortalCallback(token);
  }, [searchParams]);

  const handlePortalCallback = async (token: string) => {
    try {
      console.log('PortalCallback: Processing token authentication...');
      
      // Set the session using the token
      const { data: sessionData, error: sessionError } = await supabase.auth.setSession({
        access_token: token,
        refresh_token: token
      });

      if (sessionError) {
        console.error('PortalCallback: Session error:', sessionError);
        
        // Try alternative method using signInWithOtp or custom validation
        const { data, error } = await supabase.functions.invoke('session-info', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (error || !data?.authenticated) {
          throw new Error('Token de sessão inválido ou expirado');
        }

        // If session validation passed but setSession failed, use entry target
        setMessage('Validando sessão...');
        
        // Call get-entry-target to determine correct redirect
        try {
          const { data: entryData, error: entryError } = await supabase.functions.invoke('get-entry-target');
          
          if (!entryError && entryData?.basePath) {
            setTimeout(() => {
              navigate(entryData.basePath, { replace: true });
            }, 1000);
          } else {
            // Fallback to company select if entry target fails
            setTimeout(() => {
              navigate('/company/select', { replace: true });
            }, 1000);
          }
        } catch (error) {
          console.error('Error getting entry target:', error);
          setTimeout(() => {
            navigate('/company/select', { replace: true });
          }, 1000);
        }
        return;
      }

      console.log('PortalCallback: Session established successfully');
      setStatus('success');
      setMessage('Autenticação realizada com sucesso! Aguardando dados do usuário...');

      // Don't redirect immediately - let the useEffect handle it when data is loaded

    } catch (error: any) {
      console.error('PortalCallback: Error processing token:', error);
      setStatus('error');
      setMessage(error.message || 'Erro ao processar autenticação');
    }
  };

  const handleManualRedirect = () => {
    navigate('/login', { replace: true });
  };

  // Handle redirection when user data is fully loaded
  useEffect(() => {
    if (!authLoading && user && dataLoaded && (status === 'success' || status === 'loading')) {
      const redirectPath = getRedirectPath();
      
      if (redirectPath && redirectPath !== null) {
        console.log('PortalCallback: Redirecting to:', redirectPath);
        setMessage('Redirecionando para sua área...');
        
        // Small delay for UX
        setTimeout(() => {
          navigate(redirectPath, { replace: true });
        }, 500);
      } else if (redirectAttempts < 3) {
        // Retry if redirect path is not ready yet
        console.log('PortalCallback: Redirect path not ready, retrying...');
        setRedirectAttempts(prev => prev + 1);
        setTimeout(() => {
          // This will trigger the effect again
        }, 1000);
      } else {
        console.error('PortalCallback: Max redirect attempts reached');
        setStatus('error');
        setMessage('Erro ao determinar área de destino. Tente fazer login novamente.');
      }
    }
  }, [user, authLoading, dataLoaded, status, getRedirectPath, navigate, redirectAttempts]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/10 via-background to-secondary/10">
      <Card className="w-full max-w-md mx-4">
        <CardHeader className="text-center">
          <CardTitle className="flex items-center justify-center gap-2">
            Autenticação Portal
            <div className="w-6 h-6 bg-primary rounded flex items-center justify-center text-primary-foreground text-xs font-bold">
              P1
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          {status === 'loading' && (
            <>
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
              <p className="text-muted-foreground">
                {message || 'Processando autenticação...'}
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <div className="w-6 h-6 bg-green-500 rounded-full"></div>
              </div>
              <p className="text-green-600 font-medium">{message}</p>
            </>
          )}

          {status === 'error' && (
            <>
              <AlertCircle className="w-8 h-8 text-destructive mx-auto" />
              <p className="text-destructive font-medium">{message}</p>
              <div className="space-y-2">
                <Button onClick={handleManualRedirect} className="w-full">
                  Ir para Login Manual
                </Button>
                <p className="text-xs text-muted-foreground">
                  Se o problema persistir, entre em contato com o suporte
                </p>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}